'use client';

import { TopicDraftEditor } from './TopicDraftEditor';
import {
  encodeTopicDraft,
  draftRefs,
  appendTopicPrompts,
  removeTopicPrompt,
  reviseTopicPrompts,
  restoreTopicSampler,
} from '@/lib/onboarding/topic-draft';
import { normalizeTopicSetup } from '@/lib/prompt-topics/contract';
import { INTELLIGENCE_SETUP_MAX_BYTES } from '@/lib/intelligence-mode';

import {
  Box,
  Button,
  CloseButton,
  Collapsible,
  Dialog,
  Field,
  Flex,
  HStack,
  Input,
  NativeSelect,
  Portal,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { ArrowUp, ChevronDown, Plus, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  SAMPLER_ENGINES,
  DEFAULT_SETUP_ENGINES,
  SEARCHAPI_ENGINE_LABELS,
} from '@/lib/pipeline-constants';
import { useAuth } from '@/app/auth-provider';
import { useProject } from '@/contexts/ProjectContext';
import { CapabilityFilter } from '@/components/settings/CapabilityCatalog';
import {
  importPrompts,
  promptsFromText,
  selectPromptColumn,
  selectPromptTopicColumns,
  type PromptImportResult,
} from '@/lib/onboarding/prompt-import';
import {
  clearSetupDraft,
  normalizeSetupWebsite,
  readSetupDraft,
  sessionDraftStorage,
  setupDraftKey,
  validateWorkspaceIdentity,
  writeIntelligenceDraft,
  type IntelligenceSetupDraft,
} from '@/lib/onboarding/setup-draft';
import type {
  IntelligenceDraftRequest,
  IntelligenceDraftResponse,
  IntelligencePreparationResponse,
  IntelligenceSampler,
  IntelligenceStatusResponse,
} from '@/types/onboarding';

export interface IntelligenceSetupServices {
  status: () => Promise<IntelligenceStatusResponse>;
  saveWebsite: (website: string) => Promise<void>;
  draft: (request: IntelligenceDraftRequest) => Promise<IntelligenceDraftResponse>;
  prepare: (sampler: IntelligenceSampler) => Promise<IntelligencePreparationResponse>;
  confirm: (preparationToken: string) => Promise<IntelligenceStatusResponse>;
  retry?: (workflowId: string) => Promise<IntelligenceStatusResponse>;
}

export function createIntelligenceSetupServices(
  projectId: string,
  getToken: () => Promise<string>,
  onProjectChanged?: () => Promise<void>
): IntelligenceSetupServices {
  async function request<T>(
    suffix: string,
    body?: unknown,
    method = body === undefined ? 'GET' : 'POST'
  ): Promise<T> {
    const token = await getToken();
    const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/${suffix}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok)
      throw Object.assign(new Error(payload.error || 'Unable to save setup. Please try again.'), {
        status: response.status,
      });
    if (['setup', 'intelligence/confirm', 'intelligence/retry'].includes(suffix)) {
      // Refresh shared project settings without turning a successful save into a failed launch.
      void Promise.resolve()
        .then(onProjectChanged)
        .catch(error => console.error('[IntelligenceSetup] Project refresh failed:', error));
    }
    return payload as T;
  }
  return {
    status: () => request('intelligence/status'),
    saveWebsite: async website => {
      await request('setup', { website_url: website }, 'PATCH');
    },
    draft: body => request('intelligence/draft', body),
    prepare: sampler => request('intelligence/prepare', { sampler }),
    confirm: preparationToken => request('intelligence/confirm', { preparationToken }),
    retry: workflowId => request('intelligence/retry', { workflowId }),
  };
}

export interface IntelligenceSetupDialogViewProps {
  open: boolean;
  projectId: string;
  intelligenceMode?: 'full' | 'lite' | null;
  projectName: string;
  brandVariations?: string[];
  website: string;
  draftKey?: string;
  services: IntelligenceSetupServices;
  canManage?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfigured?: (result: IntelligenceStatusResponse) => void;
  finalFocusEl?: () => HTMLElement | null;
}

function emptyDraft(website: string): IntelligenceSetupDraft {
  return {
    prompts: [],
    pasteText: '',
    message: '',
    history: [],
    schedule: { type: 'daily_4x' },
    method: 'paste',
    website,
  };
}

function restoreIntelligenceDraft(website: string, draftKey?: string): IntelligenceSetupDraft {
  const saved = draftKey
    ? readSetupDraft<IntelligenceSetupDraft>(sessionDraftStorage(), draftKey)
    : null;
  return saved && Array.isArray(saved.prompts) && saved.schedule && Array.isArray(saved.history)
    ? { ...emptyDraft(website), ...saved }
    : emptyDraft(website);
}

export function samplerFromIntelligenceDraft(draft: IntelligenceSetupDraft): IntelligenceSampler {
  return {
    intelligence_mode: draft.intelligenceMode || 'full',
    ...encodeTopicDraft(draft),
    sample_count:
      draft.intelligenceMode === 'lite' || draft.schedule.type === 'daily_4x'
        ? 1
        : draft.sampleCount || 50,
    engines: draft.engines ?? [...DEFAULT_SETUP_ENGINES],
    engine_selection_version: 2,
    schedule:
      draft.intelligenceMode === 'lite'
        ? { type: 'daily' }
        : draft.schedule.type === 'weekly'
          ? { type: 'weekly', day_of_week: draft.schedule.day_of_week ?? 1 }
          : { type: draft.schedule.type },
    ...(draft.geoTarget?.city || draft.geoTarget?.region || draft.geoTarget?.country_code
      ? {
          geo_target: {
            ...draft.geoTarget,
            city: draft.geoTarget.city.trim(),
            region: draft.geoTarget.region.trim(),
            country_code: draft.geoTarget.country_code.trim().toUpperCase(),
          },
        }
      : {}),
  };
}

function sameSampler(a: IntelligenceSampler, b: IntelligenceSampler): boolean {
  return (
    JSON.stringify(a.prompts) === JSON.stringify(b.prompts) &&
    JSON.stringify(a.prompt_groups) === JSON.stringify(b.prompt_groups) &&
    a.sample_count === b.sample_count &&
    JSON.stringify(a.engines) === JSON.stringify(b.engines) &&
    a.intelligence_mode === b.intelligence_mode &&
    a.schedule.type === b.schedule.type &&
    a.schedule.day_of_week === b.schedule.day_of_week &&
    ['city', 'region', 'country_code', 'timezone'].every(
      key =>
        a.geo_target?.[key as keyof NonNullable<IntelligenceSampler['geo_target']>] ===
        b.geo_target?.[key as keyof NonNullable<IntelligenceSampler['geo_target']>]
    )
  );
}

/** Real setup UI without project/auth hooks; production and browser fixtures share this view. */
export function IntelligenceSetupDialogView({
  open,
  projectId,
  projectName,
  intelligenceMode,
  brandVariations = [],
  website,
  draftKey,
  services,
  canManage = true,
  onOpenChange,
  onConfigured,
  finalFocusEl,
}: IntelligenceSetupDialogViewProps) {
  const [draft, setDraft] = useState<IntelligenceSetupDraft>(() => emptyDraft(website));
  const [reviewedBrand, setReviewedBrand] = useState(() => ({
    name: projectName,
    variations: brandVariations,
  }));
  const latestDraft = useRef(draft);
  latestDraft.current = draft;
  const [hydrated, setHydrated] = useState(false);
  const [status, setStatus] = useState<IntelligenceStatusResponse | null>(null);
  const [checking, setChecking] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<'ai' | 'tracking' | null>(null);
  const [csv, setCsv] = useState<PromptImportResult['table']>();
  const [column, setColumn] = useState('');
  const [topicColumn, setTopicColumn] = useState('');
  const [hasHeader, setHasHeader] = useState(false);
  const [statusGeneration, setStatusGeneration] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const promptListRef = useRef<HTMLDivElement>(null);
  const live = useRef(true);
  const saving = useRef(false);
  const prepared = useRef<{ signature: string; token: string } | null>(null);
  const servicesRef = useRef(services);
  servicesRef.current = services;
  const initialWebsite = useRef(website);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  useEffect(() => {
    const restored = restoreIntelligenceDraft(website, draftKey);
    setDraft(
      intelligenceMode === null
        ? restored
        : {
            ...restored,
            intelligenceMode: intelligenceMode || 'full',
            ...(intelligenceMode === 'lite' ? { schedule: { type: 'daily' }, sampleCount: 1 } : {}),
          }
    );
    setHydrated(true);
  }, [draftKey, projectId]); // A project owns one draft; later website refreshes must not reset edits.

  useEffect(() => {
    if (hydrated && draftKey && status?.status !== 'ready' && status?.status !== 'collecting') {
      writeIntelligenceDraft(sessionDraftStorage(), draftKey, draft, initialWebsite.current);
    }
  }, [draft, draftKey, hydrated, status?.status]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setChecking(true);
    setStatusError(null);
    void servicesRef.current
      .status()
      .then(result => {
        if (cancelled) return;
        setStatus(result);
        if (['ready', 'collecting'].includes(result.status) && draftKey)
          clearSetupDraft(sessionDraftStorage(), draftKey);
        if (result.status === 'failed' && result.sampler) {
          setDraft(value => ({
            ...value,
            ...restoreTopicSampler(result.sampler!.prompts, result.sampler!.prompt_groups),
            intelligenceMode: result.sampler!.intelligence_mode,
            schedule: result.sampler!.schedule,
            engines: result.sampler!.engines,
            sampleCount: result.sampler!.sample_count,
            geoTarget: result.sampler!.geo_target,
          }));
        }
      })
      .catch(reason => {
        if (!cancelled)
          setStatusError(
            reason instanceof Error ? reason.message : 'Unable to check intelligence status.'
          );
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, draftKey, statusGeneration]);

  const count = draft.prompts.filter(prompt => prompt.trim()).length;
  const collectionManaged = status?.collectionManaged === true;
  const configured =
    collectionManaged || status?.status === 'collecting' || status?.status === 'ready';
  const failedCollection =
    !collectionManaged && status?.status === 'failed' && Boolean(status.workflowId);
  const disabled = collectionManaged || !canManage || busy !== null;
  function update(patch: Partial<IntelligenceSetupDraft>) {
    setDraft(value => ({ ...value, ...patch }));
    setError(null);
    setNotice(null);
  }
  function addPrompts(prompts: string[], topics?: string[]) {
    if (!prompts.length) {
      setError('Add at least one prompt.');
      return;
    }
    const currentDraft = latestDraft.current;
    const nextDraft = {
      ...appendTopicPrompts(currentDraft, prompts, topics),
      pasteText: '',
    };
    const sampler = samplerFromIntelligenceDraft(nextDraft);
    try {
      normalizeTopicSetup(sampler.prompts, sampler.prompt_groups);
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    const body = JSON.stringify({ sampler });
    if (new TextEncoder().encode(body).length > INTELLIGENCE_SETUP_MAX_BYTES) {
      setError(
        'These prompts exceed the setup size limit. Shorten the prompt text or import fewer prompts.'
      );
      return;
    }
    latestDraft.current = nextDraft;
    setDraft(nextDraft);
    setNotice(`Added ${prompts.length} ${prompts.length === 1 ? 'prompt' : 'prompts'}.`);
    setError(null);
    setCsv(undefined);
    setColumn('');
  }
  async function readFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      if (!/\.(csv|txt)$/i.test(file.name)) throw new Error('Choose a CSV or TXT file.');
      if (file.size > 1_000_000)
        throw new Error('This file is larger than 1 MB. Split it into smaller files to import.');
      const result = importPrompts(await file.text(), /\.csv$/i.test(file.name) ? 'csv' : 'txt');
      if (!live.current) return;
      if (result.table) {
        setCsv(result.table);
        setHasHeader(result.table.hasHeader);
        setColumn('');
        setTopicColumn('');
      } else addPrompts(result.prompts, result.topics);
    } catch (reason) {
      if (live.current)
        setError(reason instanceof Error ? reason.message : 'Unable to read this file.');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }
  async function saveWebsite() {
    if (!draft.website.trim()) throw new Error('Add your company website to set up Intelligence.');
    const invalid = validateWorkspaceIdentity(projectName, draft.website);
    if (invalid) throw new Error(invalid);
    const normalized = normalizeSetupWebsite(draft.website);
    if (normalized !== normalizeSetupWebsite(initialWebsite.current)) {
      await servicesRef.current.saveWebsite(normalized);
      initialWebsite.current = normalized;
    }
  }
  async function askAi() {
    if (!draft.message.trim() || disabled || saving.current) return;
    saving.current = true;
    setBusy('ai');
    setError(null);
    setNotice(null);
    const message = draft.message.trim();
    try {
      await saveWebsite();
      const response = await servicesRef.current.draft({
        message,
        history: draft.history,
        currentPrompts: draft.prompts.filter(prompt => prompt.trim()),
      });
      if (!live.current) return;
      setDraft(value => ({
        ...value,
        ...reviseTopicPrompts(value, response.prompts),
        message: '',
        history: [
          ...value.history,
          { role: 'user', content: message },
          { role: 'assistant', content: response.reply },
        ],
      }));
    } catch (reason) {
      if (live.current)
        setError(reason instanceof Error ? reason.message : 'Unable to draft prompts. Try again.');
    } finally {
      saving.current = false;
      if (live.current) setBusy(null);
    }
  }
  async function startTracking() {
    if (!count || disabled || saving.current || checking || statusError) return;
    saving.current = true;
    setBusy('tracking');
    setError(null);
    setNotice(null);
    try {
      await saveWebsite();
      const sampler = samplerFromIntelligenceDraft(draft);
      const signature = JSON.stringify({
        sampler,
        website: normalizeSetupWebsite(draft.website),
        brand: reviewedBrand,
      });
      if (!prepared.current || prepared.current.signature !== signature) {
        const result = await servicesRef.current.prepare(sampler);
        const preparedWebsite = normalizeSetupWebsite(result.normalizedDraft.website_url);
        const preparedBrand = result.normalizedDraft.brand;
        const brandChanged =
          preparedBrand.name !== reviewedBrand.name ||
          JSON.stringify(preparedBrand.variations) !== JSON.stringify(reviewedBrand.variations);
        if (preparedWebsite !== normalizeSetupWebsite(draft.website) || brandChanged) {
          initialWebsite.current = preparedWebsite;
          setDraft(value => ({ ...value, website: preparedWebsite }));
          setReviewedBrand({ name: preparedBrand.name, variations: preparedBrand.variations });
          prepared.current = null;
          throw new Error(
            brandChanged
              ? 'The company details changed. Review them below, then start tracking.'
              : 'The workspace website changed. Review it below, then start tracking.'
          );
        }
        if (!sameSampler(sampler, result.normalizedDraft.sampler)) {
          setDraft(value => ({
            ...value,
            ...restoreTopicSampler(
              result.normalizedDraft.sampler.prompts,
              result.normalizedDraft.sampler.prompt_groups
            ),
            intelligenceMode: result.normalizedDraft.sampler.intelligence_mode,
            schedule: result.normalizedDraft.sampler.schedule,
            engines: result.normalizedDraft.sampler.engines,
            sampleCount: result.normalizedDraft.sampler.sample_count,
            geoTarget: result.normalizedDraft.sampler.geo_target,
          }));
          throw new Error(
            'The collection settings were updated. Review them below, then start tracking.'
          );
        }
        prepared.current = { signature, token: result.preparationToken };
      }
      const result = await servicesRef.current.confirm(prepared.current.token);
      if (result.status === 'failed') {
        if (live.current) setStatus(result);
        throw new Error(result.error || 'Collection could not start. Retry collection to resume.');
      }
      if (draftKey) clearSetupDraft(sessionDraftStorage(), draftKey);
      onConfigured?.(result);
      if (!live.current) return;
      setStatus(result);
      onOpenChange(false);
    } catch (reason) {
      if (reason instanceof Error && 'status' in reason && reason.status === 409)
        prepared.current = null;
      if (live.current)
        setError(reason instanceof Error ? reason.message : 'Unable to start tracking. Try again.');
    } finally {
      saving.current = false;
      if (live.current) setBusy(null);
    }
  }

  async function retryCollection() {
    if (!status?.workflowId || !servicesRef.current.retry || disabled || saving.current) return;
    saving.current = true;
    setBusy('tracking');
    setError(null);
    try {
      const result = await servicesRef.current.retry(status.workflowId);
      if (result.status === 'failed')
        throw new Error(result.error || 'Collection could not resume. Try again.');
      if (draftKey) clearSetupDraft(sessionDraftStorage(), draftKey);
      onConfigured?.(result);
      if (!live.current) return;
      setStatus(result);
      onOpenChange(false);
    } catch (reason) {
      if (live.current) {
        setError(reason instanceof Error ? reason.message : 'Unable to resume collection.');
        setStatusGeneration(value => value + 1);
      }
    } finally {
      saving.current = false;
      if (live.current) setBusy(null);
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={event => onOpenChange(event.open)}
      size="xl"
      finalFocusEl={finalFocusEl}
    >
      <Portal>
        <Dialog.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(4px)" />
        <Dialog.Positioner p={{ base: '3', md: '6' }}>
          <Dialog.Content
            layerStyle="drawerSurface"
            maxW="720px"
            maxH="90dvh"
            borderRadius="8px"
            borderWidth="1px"
            borderColor="ui.border.default"
            overflow="hidden"
          >
            <Dialog.Header layerStyle="drawerHeader" pr="12" py="4">
              <Box>
                <Dialog.Title fontSize="lg" fontWeight="650" color="ui.text.primary">
                  Attune Intelligence
                </Dialog.Title>
                <Dialog.Description fontSize="sm" mt="1" color="ui.text.secondary">
                  Track the questions buyers ask about {reviewedBrand.name}.
                </Dialog.Description>
              </Box>
              <Dialog.CloseTrigger asChild position="absolute" top="3" right="3">
                <CloseButton size="sm" aria-label="Close Intelligence setup" />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body overflowY="auto" bg="ui.surface.drawer" px={{ base: '4', md: '6' }} py="4">
              {configured || failedCollection ? (
                <VStack align="stretch" gap="3" py="4">
                  <Text fontWeight="600" color="ui.text.primary">
                    {collectionManaged
                      ? 'Managed by Attune'
                      : failedCollection
                        ? 'Your collection needs attention'
                        : status?.status === 'collecting'
                          ? 'Your first collection is running'
                          : 'Intelligence is ready'}
                  </Text>
                  <Text fontSize="sm" color="ui.text.secondary">
                    {status?.promptCount} prompts across the selected engines.{' '}
                    {collectionManaged
                      ? 'This project uses a shared collection. Attune manages collection and recovery.'
                      : failedCollection
                        ? 'Your prompts and settings are saved.'
                        : status?.status === 'collecting'
                          ? 'You can keep setting up your workspace while results arrive.'
                          : 'Your results are available in the workspace.'}
                  </Text>
                  {status?.intelligenceMode === 'lite' ? (
                    <Text fontSize="sm" color="ui.text.secondary">
                      Lite · Once daily · 1 answer per prompt per engine.
                      {status.nextCollectionAt
                        ? ` Next collection: ${new Date(status.nextCollectionAt).toLocaleString()}.`
                        : ''}
                    </Text>
                  ) : null}
                  {failedCollection ? (
                    <Text fontSize="sm" color="status.warning.fg">
                      {status?.error || 'Retry to resume this collection.'}
                    </Text>
                  ) : null}
                  {error ? (
                    <Text role="alert" fontSize="sm" color="status.danger.fg">
                      {error}
                    </Text>
                  ) : null}
                </VStack>
              ) : (
                <VStack align="stretch" gap="4">
                  {intelligenceMode === null ? (
                    <Field.Root>
                      <Field.Label>Intelligence mode</Field.Label>
                      <NativeSelect.Root disabled={disabled}>
                        <NativeSelect.Field
                          aria-label="Intelligence mode"
                          value={draft.intelligenceMode || 'full'}
                          onChange={event =>
                            update({
                              intelligenceMode: event.target.value as 'full' | 'lite',
                              schedule: {
                                type: event.target.value === 'lite' ? 'daily' : 'daily_4x',
                              },
                              sampleCount: 1,
                            })
                          }
                        >
                          <option value="full">Full</option>
                          <option value="lite">Lite</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                      <Field.HelperText>
                        {draft.intelligenceMode === 'lite'
                          ? 'Once daily. Answer metrics, citations and publisher classification without cited-page scraping.'
                          : 'Full Intelligence includes cited-page scraping and analysis.'}
                      </Field.HelperText>
                    </Field.Root>
                  ) : null}
                  {reviewedBrand.variations.length ? (
                    <Text fontSize="sm" color="ui.text.secondary">
                      Also tracked as: {reviewedBrand.variations.join(', ')}
                    </Text>
                  ) : null}
                  <Field.Root>
                    <Field.Label>Company website</Field.Label>
                    <Input
                      value={draft.website}
                      onChange={event => update({ website: event.target.value })}
                      placeholder="example.com"
                      autoComplete="url"
                      disabled={disabled}
                    />
                  </Field.Root>
                  {checking ? (
                    <Text fontSize="xs" role="status" color="ui.text.tertiary">
                      Checking existing setup…
                    </Text>
                  ) : null}
                  {statusError ? (
                    <Box>
                      <Text role="alert" fontSize="sm" color="status.danger.fg">
                        {statusError}
                      </Text>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => setStatusGeneration(value => value + 1)}
                      >
                        Retry status check
                      </Button>
                    </Box>
                  ) : null}
                  {status?.status === 'failed' ? (
                    <Text fontSize="sm" color="status.warning.fg">
                      {status.error ||
                        'The previous collection needs attention. You can retry setup.'}
                    </Text>
                  ) : null}
                  <HStack gap="5" borderBottomWidth="1px" borderColor="ui.border.subtle">
                    {(
                      [
                        ['paste', 'Paste prompts'],
                        ['upload', 'Upload file'],
                        ['ai', 'Ask AI'],
                      ] as const
                    ).map(([method, label]) => (
                      <CapabilityFilter
                        key={method}
                        active={draft.method === method}
                        disabled={disabled}
                        onClick={() => update({ method })}
                      >
                        {label}
                      </CapabilityFilter>
                    ))}
                  </HStack>
                  {draft.method === 'paste' ? (
                    <VStack align="stretch" gap="2">
                      <Textarea
                        aria-label="Paste prompts, one per line"
                        placeholder="Paste your buyer questions, one per line"
                        value={draft.pasteText}
                        onChange={event => update({ pasteText: event.target.value })}
                        minH="112px"
                        resize="vertical"
                        disabled={disabled}
                        fontSize="sm"
                      />
                      <Flex justify="flex-end">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={disabled || !draft.pasteText.trim()}
                          onClick={() => addPrompts(promptsFromText(draft.pasteText))}
                        >
                          Add prompts
                        </Button>
                      </Flex>
                    </VStack>
                  ) : null}
                  {draft.method === 'upload' ? (
                    <VStack align="stretch" gap="3">
                      <Box
                        py="5"
                        px="4"
                        borderWidth="1px"
                        borderStyle="dashed"
                        borderColor="ui.border.default"
                        borderRadius="8px"
                        textAlign="center"
                        onDragOver={event => event.preventDefault()}
                        onDrop={event => {
                          event.preventDefault();
                          if (!disabled) void readFile(event.dataTransfer.files[0]);
                        }}
                      >
                        <input
                          ref={fileRef}
                          type="file"
                          accept=".csv,.txt,text/csv,text/plain"
                          aria-label="Upload prompts CSV or TXT"
                          style={{ display: 'none' }}
                          onChange={event => void readFile(event.target.files?.[0])}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fileRef.current?.click()}
                          disabled={disabled}
                        >
                          <Upload size={15} /> Choose file
                        </Button>
                        <Text mt="2" fontSize="xs" color="ui.text.tertiary">
                          CSV or TXT · or drop it here
                        </Text>
                      </Box>
                      {csv ? (
                        <VStack align="stretch" gap="2">
                          <Field.Root>
                            <Field.Label>Which column contains your prompts?</Field.Label>
                            <NativeSelect.Root>
                              <NativeSelect.Field
                                value={column}
                                onChange={event => setColumn(event.target.value)}
                              >
                                <option value="">Choose a column</option>
                                {csv.columns.map(item => (
                                  <option key={item.index} value={item.index}>
                                    {item.label}
                                    {item.preview ? ` — ${item.preview.slice(0, 65)}` : ''}
                                  </option>
                                ))}
                              </NativeSelect.Field>
                              <NativeSelect.Indicator />
                            </NativeSelect.Root>
                          </Field.Root>
                          <Field.Root>
                            <Field.Label>Topic column (optional)</Field.Label>
                            <NativeSelect.Root>
                              <NativeSelect.Field
                                aria-label="Topic CSV column"
                                value={topicColumn}
                                onChange={e => setTopicColumn(e.target.value)}
                              >
                                <option value="">No topic column</option>
                                {csv.columns.map(c => (
                                  <option key={c.index} value={c.index}>
                                    {c.label}
                                  </option>
                                ))}
                              </NativeSelect.Field>
                              <NativeSelect.Indicator />
                            </NativeSelect.Root>
                          </Field.Root>
                          <Flex gap="2" align="center" fontSize="sm">
                            <input
                              id="prompt-csv-header"
                              type="checkbox"
                              checked={hasHeader}
                              onChange={event => setHasHeader(event.target.checked)}
                            />
                            <label htmlFor="prompt-csv-header">
                              First row contains column names
                            </label>
                          </Flex>
                          <Button
                            alignSelf="flex-end"
                            size="sm"
                            variant="outline"
                            disabled={column === ''}
                            onClick={() =>
                              (() => {
                                try {
                                  const imported = selectPromptTopicColumns(
                                    csv.rows,
                                    Number(column),
                                    hasHeader,
                                    topicColumn === '' ? undefined : Number(topicColumn)
                                  );
                                  addPrompts(imported.prompts, imported.topics);
                                } catch (e) {
                                  setError((e as Error).message);
                                }
                              })()
                            }
                          >
                            Import prompts
                          </Button>
                        </VStack>
                      ) : null}
                    </VStack>
                  ) : null}
                  {draft.method === 'ai' ? (
                    <VStack align="stretch" gap="3">
                      {draft.history.length ? (
                        <VStack
                          align="stretch"
                          gap="3"
                          maxH="160px"
                          overflowY="auto"
                          aria-label="Prompt drafting conversation"
                        >
                          {draft.history.map((entry, index) => (
                            <Box
                              key={index}
                              pl={entry.role === 'user' ? '3' : '0'}
                              borderLeftWidth={entry.role === 'user' ? '2px' : '0'}
                              borderColor="ui.border.default"
                            >
                              <Text fontSize="xs" color="ui.text.tertiary" mb="1">
                                {entry.role === 'user' ? 'You' : 'Attune'}
                              </Text>
                              <Text fontSize="sm" whiteSpace="pre-wrap" color="ui.text.secondary">
                                {entry.content}
                              </Text>
                            </Box>
                          ))}
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color="ui.text.secondary">
                          Describe your buyers or what you want to learn. Attune will draft
                          questions you can edit below.
                        </Text>
                      )}
                      {draft.history.length > 0 ? (
                        <Button
                          size="xs"
                          variant="ghost"
                          alignSelf="flex-start"
                          disabled={disabled}
                          onClick={() => update({ history: [] })}
                        >
                          New conversation
                        </Button>
                      ) : null}
                      <Flex align="flex-end" gap="2">
                        <Textarea
                          aria-label="Ask AI to draft or revise prompts"
                          placeholder="What would buyers ask when comparing us with alternatives?"
                          value={draft.message}
                          onChange={event => update({ message: event.target.value })}
                          minH="80px"
                          disabled={disabled}
                          fontSize="sm"
                        />
                        <Button
                          aria-label="Send to AI"
                          size="sm"
                          loading={busy === 'ai'}
                          disabled={disabled || !draft.message.trim()}
                          onClick={() => void askAi()}
                        >
                          <ArrowUp size={16} />
                        </Button>
                      </Flex>
                    </VStack>
                  ) : null}
                  {draft.prompts.length ? (
                    <TopicDraftEditor draft={draft} onChange={update} disabled={disabled} />
                  ) : null}
                  {draft.prompts.length ? (
                    <Box>
                      <Flex align="center" justify="space-between" mb="2">
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color="ui.text.primary"
                          aria-live="polite"
                        >
                          {count} {count === 1 ? 'prompt' : 'prompts'}
                        </Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          disabled={disabled}
                          onClick={() => {
                            const next = emptyDraft(initialWebsite.current);
                            setDraft(next);
                            setCsv(undefined);
                            setNotice(null);
                            setError(null);
                            if (draftKey) clearSetupDraft(sessionDraftStorage(), draftKey);
                          }}
                        >
                          Clear draft
                        </Button>
                      </Flex>
                      <VStack
                        ref={promptListRef}
                        align="stretch"
                        gap="1.5"
                        maxH="240px"
                        overflowY="auto"
                        pr="1"
                        aria-label="Editable prompts"
                      >
                        {draft.prompts.map((prompt, index) => (
                          <Flex key={index} align="flex-start" gap="2">
                            <Text minW="5" pt="2" fontSize="xs" color="ui.text.tertiary">
                              {index + 1}
                            </Text>
                            <Textarea
                              aria-label={`Prompt ${index + 1}`}
                              value={prompt}
                              rows={1}
                              minH="38px"
                              css={{ fieldSizing: 'content' }}
                              overflowY="hidden"
                              resize="vertical"
                              fontSize="sm"
                              disabled={disabled}
                              onChange={event => {
                                const element = event.target;
                                element.style.height = 'auto';
                                element.style.height = `${element.scrollHeight}px`;
                                setDraft(value => ({
                                  ...value,
                                  prompts: value.prompts.map((item, i) =>
                                    i === index ? event.target.value : item
                                  ),
                                }));
                              }}
                            />
                            <Button
                              aria-label={`Remove prompt ${index + 1}`}
                              size="xs"
                              mt="1"
                              variant="ghost"
                              disabled={disabled}
                              onClick={() =>
                                setDraft(value => ({
                                  ...removeTopicPrompt(value, index),
                                }))
                              }
                            >
                              <X size={14} />
                            </Button>
                          </Flex>
                        ))}
                      </VStack>
                      <Button
                        size="xs"
                        variant="ghost"
                        mt="2"
                        disabled={disabled}
                        onClick={() => {
                          setDraft(value => ({
                            ...value,
                            promptRefs: [
                              ...draftRefs(value),
                              crypto.randomUUID().replaceAll('-', ''),
                            ],
                            prompts: [...value.prompts, ''],
                          }));
                          requestAnimationFrame(() => {
                            const fields = promptListRef.current?.querySelectorAll('textarea');
                            fields?.[fields.length - 1]?.focus();
                          });
                        }}
                      >
                        <Plus size={14} /> Add a prompt
                      </Button>
                    </Box>
                  ) : null}
                  <Box borderTopWidth="1px" borderColor="ui.border.subtle" pt="3">
                    <HStack justify="space-between">
                      <Text fontSize="sm">Engines</Text>
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={disabled}
                        onClick={() =>
                          setDraft(value => ({ ...value, engines: [...SAMPLER_ENGINES] }))
                        }
                      >
                        Select all
                      </Button>
                    </HStack>
                    <Flex gap="2" wrap="wrap" aria-label="Sampling engines">
                      {SAMPLER_ENGINES.map(engine => {
                        const selected = (draft.engines ?? DEFAULT_SETUP_ENGINES).includes(engine);
                        const labels: Record<string, string> = {
                          chatgpt: 'ChatGPT (Direct)',
                          gemini: 'Gemini (Direct)',
                          perplexity: 'Perplexity (Direct)',
                          grok: 'Grok',
                          google_aio: 'Google AI Overviews',
                          ...SEARCHAPI_ENGINE_LABELS,
                        };
                        return (
                          <Button
                            key={engine}
                            size="xs"
                            variant={selected ? 'solid' : 'outline'}
                            aria-pressed={selected}
                            disabled={disabled}
                            onClick={() =>
                              setDraft(value => ({
                                ...value,
                                engines: selected
                                  ? (value.engines ?? DEFAULT_SETUP_ENGINES).filter(
                                      item => item !== engine
                                    )
                                  : [...(value.engines ?? DEFAULT_SETUP_ENGINES), engine],
                              }))
                            }
                          >
                            {labels[engine]}
                          </Button>
                        );
                      })}
                    </Flex>
                    {draft.engines?.length === 0 ? (
                      <Text color="red.500" fontSize="xs">
                        Select at least one engine.
                      </Text>
                    ) : null}

                    <Collapsible.Root>
                      <Collapsible.Trigger asChild>
                        <Button
                          variant="ghost"
                          size="xs"
                          px="0"
                          fontWeight="500"
                          color="ui.text.secondary"
                        >
                          {draft.intelligenceMode === 'lite'
                            ? 'Once daily · 1 answer per prompt per engine'
                            : draft.schedule.type === 'daily_4x'
                              ? '4 times daily · 1 sample per prompt'
                              : draft.schedule.type === 'weekly'
                                ? `Weekly · ${draft.sampleCount || 50} samples per prompt`
                                : `One collection · ${draft.sampleCount || 50} samples per prompt`}
                          {draft.geoTarget?.city ? ` · ${draft.geoTarget.city}` : ''}
                          <ChevronDown size={14} />
                        </Button>
                      </Collapsible.Trigger>
                      <Collapsible.Content>
                        <VStack align="stretch" gap="3" pt="2">
                          {draft.intelligenceMode !== 'lite' ? (
                            <Field.Root>
                              <Field.Label>Collection frequency</Field.Label>
                              <NativeSelect.Root disabled={disabled}>
                                <NativeSelect.Field
                                  value={draft.schedule.type}
                                  onChange={event =>
                                    update({
                                      schedule:
                                        event.target.value === 'weekly'
                                          ? { type: 'weekly', day_of_week: 1 }
                                          : { type: event.target.value as 'daily_4x' | 'one_off' },
                                    })
                                  }
                                >
                                  <option value="daily_4x">4 times daily</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="one_off">Once</option>
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                              </NativeSelect.Root>
                            </Field.Root>
                          ) : null}
                          {draft.schedule.type === 'weekly' ? (
                            <Field.Root>
                              <Field.Label>Day</Field.Label>
                              <NativeSelect.Root disabled={disabled}>
                                <NativeSelect.Field
                                  value={draft.schedule.day_of_week ?? 1}
                                  onChange={event =>
                                    update({
                                      schedule: {
                                        type: 'weekly',
                                        day_of_week: Number(event.target.value),
                                      },
                                    })
                                  }
                                >
                                  {[
                                    'Sunday',
                                    'Monday',
                                    'Tuesday',
                                    'Wednesday',
                                    'Thursday',
                                    'Friday',
                                    'Saturday',
                                  ].map((day, index) => (
                                    <option key={day} value={index}>
                                      {day}
                                    </option>
                                  ))}
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                              </NativeSelect.Root>
                            </Field.Root>
                          ) : null}
                          {draft.intelligenceMode !== 'lite' &&
                          draft.schedule.type !== 'daily_4x' ? (
                            <Field.Root>
                              <Field.Label>Samples per prompt</Field.Label>
                              <NativeSelect.Root disabled={disabled}>
                                <NativeSelect.Field
                                  value={draft.sampleCount || 50}
                                  onChange={event =>
                                    update({ sampleCount: Number(event.target.value) })
                                  }
                                >
                                  {[10, 20, 50, 100, 200, 400].map(value => (
                                    <option key={value} value={value}>
                                      {value}
                                    </option>
                                  ))}
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                              </NativeSelect.Root>
                            </Field.Root>
                          ) : null}
                          <Text fontSize="xs" color="ui.text.tertiary">
                            Location (optional)
                          </Text>
                          <Flex direction={{ base: 'column', sm: 'row' }} gap="2">
                            {(
                              [
                                ['city', 'City'],
                                ['region', 'State / region'],
                                ['country_code', 'Country code'],
                              ] as const
                            ).map(([key, label]) => (
                              <Field.Root key={key}>
                                <Field.Label fontSize="xs">{label}</Field.Label>
                                <Input
                                  size="sm"
                                  disabled={disabled}
                                  value={draft.geoTarget?.[key] || ''}
                                  placeholder={key === 'country_code' ? 'US' : label}
                                  onChange={event =>
                                    update({
                                      geoTarget: {
                                        city: '',
                                        region: '',
                                        country_code: '',
                                        ...draft.geoTarget,
                                        [key]: event.target.value,
                                      },
                                    })
                                  }
                                />
                              </Field.Root>
                            ))}
                          </Flex>
                        </VStack>
                      </Collapsible.Content>
                    </Collapsible.Root>
                  </Box>
                  {notice ? (
                    <Text fontSize="xs" role="status" color="ui.text.secondary">
                      {notice}
                    </Text>
                  ) : null}
                  {error ? (
                    <Text role="alert" fontSize="sm" color="status.danger.fg">
                      {error}
                    </Text>
                  ) : null}
                  {!canManage ? (
                    <Text fontSize="sm" color="ui.text.secondary">
                      A workspace owner or admin can configure Intelligence.
                    </Text>
                  ) : null}
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer
              borderTopWidth="1px"
              borderColor="ui.border.subtle"
              px={{ base: '4', md: '6' }}
              py="3"
            >
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                {configured ? 'Done' : 'Close'}
              </Button>
              {failedCollection ? (
                <Button
                  size="sm"
                  onClick={() => void retryCollection()}
                  loading={busy === 'tracking'}
                  loadingText="Resuming…"
                  disabled={disabled || checking || !services.retry}
                >
                  Retry collection
                </Button>
              ) : !configured ? (
                <Button
                  size="sm"
                  onClick={() => void startTracking()}
                  loading={busy === 'tracking'}
                  loadingText="Starting…"
                  disabled={!count || disabled || checking || Boolean(statusError)}
                >
                  Start tracking
                </Button>
              ) : null}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export function IntelligenceSetupDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfigured?: (result: IntelligenceStatusResponse) => void;
  finalFocusEl?: () => HTMLElement | null;
}) {
  const { user } = useAuth();
  const { currentProject, fetchProjects } = useProject();
  if (!user || !currentProject || currentProject.intelligence_collection_linked) return null;
  return (
    <IntelligenceSetupDialogView
      key={`${user.uid}:${currentProject.id}`}
      {...props}
      projectId={currentProject.id}
      intelligenceMode={currentProject.intelligence_mode}
      projectName={currentProject.brand_settings.brand_name || currentProject.name}
      brandVariations={currentProject.brand_settings.brand_variations || []}
      website={currentProject.brand_settings.website_url || ''}
      draftKey={setupDraftKey(user.uid, currentProject.id, 'intelligence')}
      canManage={
        currentProject.current_user_role === 'owner' || currentProject.current_user_role === 'admin'
      }
      services={createIntelligenceSetupServices(
        currentProject.id,
        () => user.getIdToken(),
        () => fetchProjects(true)
      )}
    />
  );
}
